import { Button, Spacer } from '@odekoteam/doppio';
import { useEffect, useState } from 'react';

import {
  CustomerPickupTarget,
  sameId,
  useUpdateLocationCustomerPickup,
} from '../LocationsTable/useUpdateLocationCustomerPickup';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { modalSelector } from '../../state/selectors/modal';
import { hideModal } from '../../state/slices/modal';
import { formatISO8601Date } from '../../utils/date';
import CancelButton from './CancelButton';
import { buttonContainer, styledConfirmationModal } from './styles';

export type RecentDeliveryOffender = {
  locationId: string;
  name?: string;
  stopCount: number;
  lastNightEnd?: string;
};

export const RECENT_DELIVERIES_TITLE = 'This location took recent deliveries';
// The consequence ops is actually deciding on, from the backend's own report:
// flagging is not necessarily wrong, but from then on the location's stops land
// in a bucket that is never driven.
export const RECENT_DELIVERIES_BODY =
  'It had stops on a driven route in the last 90 nights. Once it is flagged, its stops land in Customer Pick Ups, which no driver ever runs: an occasional delivery becomes a manual move out of the bucket.';
export const RECENT_DELIVERIES_BULK_BODY =
  'They had stops on a driven route in the last 90 nights. Once they are flagged, their stops land in Customer Pick Ups, which no driver ever runs: an occasional delivery becomes a manual move out of the bucket.';
export const RECENT_DELIVERIES_BILLING =
  'Orders completed there are billed at fulfilled quantity, with no driver capture and no delta review.';

// The backend only inspects rows the write would change, so the offenders are
// always a subset of those and the denominator is their count, not the whole
// selection.
export const bulkRecentDeliveriesTitle = (
  offenderCount: number,
  changingCount: number,
): string =>
  `${offenderCount} of ${changingCount} locations took recent deliveries`;

const offenderLine = (offender: RecentDeliveryOffender): string => {
  const stops = `${offender.stopCount} ${
    offender.stopCount === 1 ? 'stop' : 'stops'
  }`;
  // lastNightEnd is a bare ISO date. new Date() on one is parsed as UTC and
  // rendered in local time, which shows the previous day everywhere in the US.
  const last = offender.lastNightEnd
    ? `, last on ${formatISO8601Date(offender.lastNightEnd)}`
    : '';
  return `${stops}${last}`;
};

export function CustomerPickupRecentDeliveriesModal(): JSX.Element {
  const dispatch = useAppDispatch();
  const { variables } = useAppSelector(modalSelector);
  const locationsContextId = useAppSelector(
    (state) => state.locations.metrobi?.contextId,
  );
  const { submit, isSubmitting } = useUpdateLocationCustomerPickup();

  // Snapshot at mount for the same reason the confirmation dialog does: one
  // shared modal slice, and a later showModal would swap the target out from
  // under an in-flight re-submit. A repeat rejection is a fresh mount, not a
  // re-render, because Modal keys this dialog on the request's nonce.
  const [request] = useState<{
    locations: CustomerPickupTarget[];
    notFoundIds: string[];
    skipped: CustomerPickupTarget[];
    unknown: CustomerPickupTarget[];
    businessName?: string;
    contextId?: string;
    returnFocusId?: string;
    offenders: RecentDeliveryOffender[];
  }>(() => {
    const locations = (variables?.locations ??
      // The single-location shape the row switch still sends.
      (variables?.locationId
        ? [{ id: variables.locationId, businessName: variables?.businessName }]
        : [])) as CustomerPickupTarget[];
    return {
      locations,
      notFoundIds: (variables?.notFoundIds ?? []) as string[],
      skipped: (variables?.skipped ?? []) as CustomerPickupTarget[],
      unknown: (variables?.unknown ?? []) as CustomerPickupTarget[],
      businessName: variables?.businessName,
      contextId: variables?.contextId,
      returnFocusId: variables?.returnFocusId,
      offenders: (variables?.offenders ?? []) as RecentDeliveryOffender[],
    };
  });

  useEffect(() => {
    if (request.contextId && request.contextId !== locationsContextId)
      dispatch(hideModal());
  }, [request.contextId, locationsContextId, dispatch]);

  // Opening from the bulk dialog unmounts it, and its cleanup returns focus to
  // the Edit button behind this one. A useEffect runs after that cleanup; a
  // layout effect would run before it and lose the focus it just claimed.
  // Cancel rather than the primary button, because a stray Enter must not
  // acknowledge a warning the operator has not read.
  useEffect(() => {
    document.getElementById('confirmation-modal-dismiss-button')?.focus();
  }, []);

  const single = request.locations.length === 1;
  const offenderCount = request.offenders.length;
  // Everything the operator has already chosen to leave out, plus what this
  // round would leave out, so the final message can still name all of them.
  const remaining = request.locations.filter(
    (location) =>
      !request.offenders.some((offender) =>
        sameId(offender.locationId, location.id),
      ),
  );
  const offenderTargets = request.locations.filter((location) =>
    request.offenders.some((offender) =>
      sameId(offender.locationId, location.id),
    ),
  );
  // What a skip submits: the rows left over, minus any the server has just said
  // it does not know. Re-sending an unknown id would only earn the same
  // not-found back. Rows that already read as flagged stay in, because the
  // operator's table can be wrong about them and the server decides.
  const remainingKnown = remaining.filter(
    (location) => !request.notFoundIds.some((id) => sameId(id, location.id)),
  );
  // Locations this reply called unknown, plus any an earlier round did. Not
  // re-sent, but carried so the final message still names them.
  const unknownTargets = [
    ...request.unknown,
    ...remaining.filter((location) =>
      request.notFoundIds.some((id) => sameId(id, location.id)),
    ),
  ];
  // Skip is offered whenever any known location survives it, without asking
  // whether those rows would change. That question can only be answered from
  // the operator's table, which can be wrong, and answering it wrongly hides
  // Skip and strands a location that does need flagging.
  const canSkip = !single && remainingKnown.length > 0;
  // What the guard looked at: the offenders plus the known rows it passed over.
  const changingCount = offenderCount + remainingKnown.length;

  const nameById = new Map(
    request.locations.map((location) => [
      location.id.toLowerCase(),
      location.businessName,
    ]),
  );

  const handleOnClickConfirm = async (): Promise<void> => {
    if (!request.locations.length) {
      dispatch(hideModal());
      return;
    }
    await submit({
      locations: request.locations,
      customerPickup: true,
      contextId: request.contextId,
      returnFocusId: request.returnFocusId,
      acknowledgeRecentDeliveries: true,
      skipped: request.skipped,
      unknown: unknownTargets,
    });
  };

  // No acknowledgement: the guard only objects to the offenders, so the
  // complement passes it on its own. A second rejection is possible, because
  // the window has no lower bound and a row can start offending between the
  // two submits.
  // Only rendered while `remainingKnown` is non-empty, so there is always
  // something to send.
  const handleOnClickSkip = async (): Promise<void> => {
    await submit({
      locations: remainingKnown,
      customerPickup: true,
      contextId: request.contextId,
      returnFocusId: request.returnFocusId,
      skipped: [...request.skipped, ...offenderTargets],
      unknown: unknownTargets,
    });
  };

  return (
    <div
      id="customer-pickup-recent-deliveries-modal"
      data-test-id="customer-pickup-recent-deliveries-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-pickup-recent-deliveries-title"
      sx={{
        ...styledConfirmationModal,
        textAlign: 'left',
        maxWidth: '460px',
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      <div
        id="customer-pickup-recent-deliveries-title"
        sx={{ variant: 'text.headingSm' }}
      >
        {single
          ? RECENT_DELIVERIES_TITLE
          : bulkRecentDeliveriesTitle(offenderCount, changingCount)}
      </div>
      <Spacer height="$3" />
      <div sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}>
        {single ? RECENT_DELIVERIES_BODY : RECENT_DELIVERIES_BULK_BODY}
      </div>
      <Spacer height="$2" />
      <div sx={{ variant: 'text.paragraphMdDefault', color: 'secondaryText' }}>
        {RECENT_DELIVERIES_BILLING}
      </div>
      <Spacer height="$3" />
      <ul
        data-test-id="customer-pickup-offenders"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: '10px 14px',
          backgroundColor: 'backgroundWeak',
          border: '1px solid',
          borderColor: 'backgroundMedium',
          borderRadius: '8px',
          variant: 'text.paragraphMdDefault',
        }}
      >
        {request.offenders.map((offender) => (
          <li key={offender.locationId} sx={{ paddingY: '4px' }}>
            {offender.name ||
              nameById.get(offender.locationId.toLowerCase()) ||
              request.businessName ||
              'Unknown location'}
            <span sx={{ color: 'secondaryText' }}>
              {' '}
              ({offenderLine(offender)})
            </span>
          </li>
        ))}
      </ul>
      <div sx={{ ...buttonContainer, justifyContent: 'flex-start' }}>
        <Button
          variant="primary"
          id="customer-pickup-acknowledge-action-button"
          data-test-id="customer-pickup-acknowledge-action-button"
          onPress={handleOnClickConfirm}
          isDisabled={isSubmitting}
        >
          {isSubmitting
            ? 'Saving...'
            : single
            ? 'Flag anyway'
            : 'Flag all anyway'}
        </Button>
        <Spacer width="$2" />
        {canSkip && (
          <>
            <Button
              variant="secondary"
              id="customer-pickup-skip-action-button"
              data-test-id="customer-pickup-skip-action-button"
              onPress={handleOnClickSkip}
              isDisabled={isSubmitting}
            >
              {`Skip these ${offenderCount} and flag the rest`}
            </Button>
            <Spacer width="$2" />
          </>
        )}
        <CancelButton
          variant="secondary"
          returnFocusId={request.returnFocusId}
          isDisabled={isSubmitting}
        />
      </div>
    </div>
  );
}
